import { Component, OnInit, Type } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, flatMap, map, switchMap } from 'rxjs/operators';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { DialogButtonColour, DialogComponent } from '@shared/components/dialog/dialog.component';
import { ModalSetting } from '@shared/components/modal/modal-setting';
import { DiaryNoteService } from '@features/diary-notes/services/diary-note.service';
import { Guid } from 'guid-typescript';
import { DiaryNote } from '@features/diary-notes/model/diary-note';
import { compareDates } from '@shared/utilities/typescript-utils';
import { ActivatedRoute } from '@angular/router';
import { DiaryNoteEditComponent } from '../custom/common/edit/diary-note-edit.component';
import { OpportunityDiaryNoteEditComponent } from '../custom/opportunity/edit/opportunity-diary-note-edit.component';
import { DiaryNoteNewComponent } from '../custom/common/new/diary-note-new.component';
import { OpportunityDiaryNoteNewComponent } from '../custom/opportunity/new/opportunity-diary-note-new.component';
import { DiaryNoteContextMenuComponent } from '../../pipes/diary-note-context-menu.pipe';

type DiaryNoteDetailComponentType = 'common' | 'opportunity';

type ComponentRegistry = {
  ['common']: Type<any>;
  ['opportunity']: Type<any>;
};


@Component({
  templateUrl: './diary-note-list.component.html',
  styleUrls: ['./diary-note-list.component.scss']
})
export class DiaryNoteListComponent implements OnInit, DiaryNoteContextMenuComponent {

  entityId$ = this.diaryNoteService.entityId$; 
  entityId!: string;
  type: DiaryNoteDetailComponentType = 'common';

  editComponentRegistry : ComponentRegistry = {
    ['common']: DiaryNoteEditComponent,
    ['opportunity']: OpportunityDiaryNoteEditComponent
  };  

  newComponentRegistry : ComponentRegistry = {
    ['common']: DiaryNoteNewComponent,
    ['opportunity']: OpportunityDiaryNoteNewComponent
  };

  public search$: BehaviorSubject<[string]> = new BehaviorSubject<[string]>(['']);

  modal: BsModalRef | null = null;
  subscriptions: Subscription[] = [];

  searchControl: FormControl = new FormControl<string | null>(null);
  public form: FormGroup = this.formBuilder.group({
    searchControl: this.searchControl
  });

  public load$: Observable<DiaryNote.Model[]> = this.entityId$.pipe(
    flatMap(entityId => {
      this.entityId = entityId;
      return this.search$.pipe(
        switchMap(([searchString]) =>
          this.diaryNoteService
            .getDiaryNotes(this.entityId, '')
            .pipe(
              map((notes) => {
                let filteredNotes = notes
                  .filter((x) => x.details?.search(new RegExp(searchString, 'i')) != -1)
                  .map((x) => {
                    let regEx = new RegExp(searchString, 'ig');
                    let replaceMask = `<mark>${searchString}</mark>`;
  
                    return {
                      summary: x.summary,
                      details: x.details?.replace(regEx, replaceMask)
                    } as DiaryNote.Model;
                  });
                const result = searchString != null && searchString.length > 0 ? filteredNotes : notes;
                result.sort((a, b) => compareDates(b.createdDate, a.createdDate));
                return result;
              }
            )
        )
      ))
      }
    ));

  constructor(
    protected modalService: BsModalService,
    protected diaryNoteService: DiaryNoteService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }



  protected refreshList(): void {
    this.search$.next([this.searchControl.value]);
  }

  private subscribeToChanges(): void {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((_) => {
          this.refreshList();
        })
    );

    this.subscriptions.push(
      this.route
      .data
      .subscribe(v => {
        const routeInputType = v.type as DiaryNoteDetailComponentType;
        if (routeInputType) {
          this.type = routeInputType ;
        }
      })       
    );
  }

  protected closeModal(): void {
    this.modal?.hide();
  };

  protected closeAndRefresh(): void {
    this.closeModal();
    this.refreshList();
  }

  public deleteNote(noteId: string): void {
    const initialState = {
      title: "Delete Note",
      message: "This action cannot be undone.",
      buttons: [
        { label: 'Cancel',
          action: of([]),
          complete: () => this.closeModal(),
          colour: DialogButtonColour.default
        },
        { label: 'Delete',
          action: this.delete(noteId),
          complete: () => this.closeAndRefresh(),
          colour: DialogButtonColour.danger
        }
      ]
    }

    this.modal = this.modalService.show(
      DialogComponent,
      ModalSetting.createDialog(initialState))
  }

  public editNote(noteId: string): void {
    this.closeModal();
    this.openModal(this.editComponentRegistry[this.type], noteId);
  }

  public addNote(): void {
    this.closeModal();
    this.openModal(this.newComponentRegistry[this.type]);
  }

  private openModal(component: any, noteId?: string): void {
    this.modal = this.modalService.show(
      component,
      ModalSetting.createLargeModal(
        {
          entityId: this.entityId,
          diaryNoteId: noteId? noteId : Guid.create().toString(),
          cancel: () => this.closeModal(),
          saved: () => this.closeAndRefresh()
        },
        'details-modal'
      )
    );
  }

  public delete(noteId: string): Observable<any> {
    return this.diaryNoteService.deleteDiaryNote(this.entityId, noteId);
  }
}
